import type { RefObject } from 'react';
import { useLayoutEffect } from 'react';
import { stackOffset, stackOffsetCss } from '~/modules/common/sticky-box';
import { getScrollParent } from '~/utils/scroll-parent';

/**
 * Pins measured header cells with fixed positioning below the sticky stack (the bars `StickyBox` publishes), syncing
 * horizontal offsets in animation frames. Only a grid the window scrolls is pinned: fixed cells measure from the
 * viewport, which is not the pin line of a sheet, drawer or dialog that scrolls its own content.
 */
export function useStickyHeader(gridRef: RefObject<HTMLDivElement | null>, headerRowsCount: number, headerRowHeight: number, enabled: boolean) {
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!enabled || !grid || headerRowsCount === 0 || getScrollParent(grid) !== window) return;

    const headerRowsHeight = headerRowsCount * headerRowHeight;
    // Separate rAF IDs so different concerns don't cancel each other
    let scrollRafId = 0;
    let hScrollRafId = 0;
    let resizeRafId = 0;
    let resizeSettleTimer = 0;
    let isSticky = false;
    // Grid bounds of the last horizontal sync: vertical scrolling leaves them unchanged, so it skips the per-cell writes.
    let syncedLeft = Number.NaN;
    let syncedRight = Number.NaN;
    let headerCells: HTMLElement[] = [];
    let originalStyles: { cssText: string }[] = [];

    function getHeaderCells(): HTMLElement[] {
      return Array.from(grid!.querySelectorAll<HTMLElement>('.rdg-header-row > .rdg-cell'));
    }

    function applyFixed() {
      headerCells = getHeaderCells();
      originalStyles = headerCells.map((cell) => ({ cssText: cell.style.cssText }));

      // Measure each cell's bounding rect before changing position
      const rects = headerCells.map((cell) => cell.getBoundingClientRect());

      for (let i = 0; i < headerCells.length; i++) {
        const cell = headerCells[i];
        const rect = rects[i];
        cell.style.position = 'fixed';
        cell.style.zIndex = '20';
        cell.style.top = stackOffsetCss();
        cell.style.left = `${rect.left}px`;
        cell.style.width = `${rect.width}px`;
        cell.style.height = `${headerRowHeight}px`;
        cell.style.display = 'flex';
        cell.style.backgroundColor = 'var(--background)';
      }

      // The grid's template rows keep reserving the header height, so rows don't jump up.
      isSticky = true;
      grid!.dataset.headerPinned = '';
    }

    function removeFixed() {
      for (let i = 0; i < headerCells.length; i++) {
        headerCells[i].style.cssText = originalStyles[i].cssText;
      }
      headerCells = [];
      originalStyles = [];
      isSticky = false;
      delete grid!.dataset.headerPinned;
    }

    function syncHorizontal(rect?: DOMRect) {
      if (!isSticky || headerCells.length === 0) return;
      const gridRect = rect ?? grid!.getBoundingClientRect();
      const scrollLeft = grid!.scrollLeft;
      const gridLeft = gridRect.left;
      const gridRight = gridRect.right;
      syncedLeft = gridLeft;
      syncedRight = gridRight;

      let currentLeft = gridLeft - scrollLeft;
      for (const cell of headerCells) {
        const w = Number.parseFloat(cell.style.width);
        cell.style.left = `${currentLeft}px`;

        // Hide cells that are fully outside the grid's visible bounds
        const cellRight = currentLeft + w;
        cell.style.visibility = cellRight <= gridLeft || currentLeft >= gridRight ? 'hidden' : '';

        currentLeft += w;
      }
    }

    /** Re-measure widths from measuring cells and apply to fixed header cells */
    function syncWidths() {
      if (!isSticky || headerCells.length === 0) return;
      const measuringCells = grid!.querySelectorAll<HTMLElement>('[data-measuring-cell-key]');
      if (measuringCells.length === 0) return;

      const widths: number[] = [];
      for (const mc of measuringCells) {
        widths.push(mc.getBoundingClientRect().width);
      }

      for (let i = 0; i < headerCells.length && i < widths.length; i++) {
        headerCells[i].style.width = `${widths[i]}px`;
      }

      syncHorizontal();
    }

    function update() {
      const rect = grid!.getBoundingClientRect();
      // Read per frame: a bar above can change height, or leave in focus view, without the grid rendering.
      const pinTop = stackOffset(grid!);
      const shouldStick = rect.top < pinTop && rect.bottom > pinTop + headerRowsHeight;

      if (shouldStick && !isSticky) {
        applyFixed();
        syncHorizontal(rect);
      } else if (!shouldStick && isSticky) {
        removeFixed();
      } else if (isSticky && (rect.left !== syncedLeft || rect.right !== syncedRight)) {
        syncHorizontal(rect);
      }
    }

    const onScrollVertical = () => {
      cancelAnimationFrame(scrollRafId);
      scrollRafId = requestAnimationFrame(update);
    };

    const onScrollHorizontal = () => {
      if (!isSticky) return;
      cancelAnimationFrame(hScrollRafId);
      hScrollRafId = requestAnimationFrame(() => syncHorizontal());
    };

    const onResize = () => {
      if (!isSticky) return;
      cancelAnimationFrame(resizeRafId);
      resizeRafId = requestAnimationFrame(syncWidths);
      // Trailing settle: guarantee final sync after resize activity stops
      clearTimeout(resizeSettleTimer);
      resizeSettleTimer = window.setTimeout(syncWidths, 150);
    };

    window.addEventListener('scroll', onScrollVertical, { passive: true });
    window.addEventListener('resize', onScrollVertical, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    // Track horizontal scroll on the grid itself (it has overflow-x: auto)
    grid.addEventListener('scroll', onScrollHorizontal, { passive: true });

    // Watch measuring cells for width changes (triggered by column resize)
    const resizeObserver = new ResizeObserver(onResize);
    for (const mc of grid.querySelectorAll('[data-measuring-cell-key]')) {
      resizeObserver.observe(mc);
    }

    update();

    return () => {
      cancelAnimationFrame(scrollRafId);
      cancelAnimationFrame(hScrollRafId);
      cancelAnimationFrame(resizeRafId);
      clearTimeout(resizeSettleTimer);
      resizeObserver.disconnect();
      window.removeEventListener('scroll', onScrollVertical);
      window.removeEventListener('resize', onScrollVertical);
      window.removeEventListener('resize', onResize);
      grid.removeEventListener('scroll', onScrollHorizontal);
      if (isSticky) removeFixed();
    };
  }, [gridRef, headerRowsCount, headerRowHeight, enabled]);
}
